import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import styles from './Forms.module.css';

import TextInput from '../UILibrary/TextInput';
import Submission from '../UILibrary/Submission';
import Upload from '../UILibrary/Upload';
import Close from '../../../Utils/Assets/SVGs/Close';

import useImageUploadRules from '../../../Logic/Functions/Hooks/useImageUploadRules';
import useProductFormLogic from '../../../Logic/Functions/Hooks/useProductFormLogic';
import { ProductsService } from '../../../Logic/ProductsService';

import type { ExistingUploadFile } from '@model/UIComponents.model';
import type { EditProductFormProps, ProductSpec, IProductData } from '@model/Products.model';
import type { ICreateProductProps } from '@model/Category.model';
import { defaultEditValues } from 'Utils/Global/globalConstants';
import { UserAuthenticationStoreImplementation } from 'Data/UserAuthentication/UserAuthenticationStoreImplementation';
import { useForceLogoutAndRedirect } from '../../../Logic/Functions/Hooks/useForceLogoutAndRedirect';
import { UnauthorizedPopupStoreImplementation } from 'Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../Modals/ConfirmModal';

const EditProductForm: React.FC<EditProductFormProps> = ({ slug }) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<ICreateProductProps>({
    mode: 'onSubmit',
    defaultValues: defaultEditValues,
  });
  const NAME_MAX = Number(process.env.REACT_APP_PRODUCT_NAME_MAX) || 120;
  const SHORT_DESC_MAX = Number(process.env.REACT_APP_PRODUCT_SHORT_DESC_MAX) || 200;
  const LONG_DESC_MAX = Number(process.env.REACT_APP_PRODUCT_LONG_DESC_MAX) || 500;
  const MAX_IMAGES = Number(process.env.REACT_APP_PRODUCT_MAX_IMAGES) || 5;
  const MAX_IMAGE_MB = Number(process.env.REACT_APP_PRODUCT_MAX_IMAGE_MB) || 5;
  const MAX_FILES = Number(process.env.REACT_APP_PRODUCT_MAX_FILES) || 5;
  const MAX_PDF_MB = Number(process.env.REACT_APP_PRODUCT_MAX_PDF_MB) || 10;
  const productsService = new ProductsService();
  const [apiError, setApiError] = useState<string | undefined>();
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedProduct, setLoadedProduct] = useState<IProductData | null>(null);
  const [existingImageFiles, setExistingImageFiles] = useState<ExistingUploadFile[]>([]);
  const [existingPdfFiles, setExistingPdfFiles] = useState<ExistingUploadFile[]>([]);
  const [removedExistingImageIndexes, setRemovedExistingImageIndexes] = useState<number[]>([]);
  const [isExistingPdfRemoved, setIsExistingPdfRemoved] = useState(false);
  const [didSave, setDidSave] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingDeleteImage, setPendingDeleteImage] = useState<ExistingUploadFile | null>(null);
  const authStore = UserAuthenticationStoreImplementation();
  const onAuthError = useForceLogoutAndRedirect();
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const navigate = useNavigate();
  const nameValue = useWatch({ control, name: 'name' }) ?? '';
  const shortDescValue = useWatch({ control, name: 'shortDescription' }) ?? '';
  const longDescValue = useWatch({ control, name: 'longDescription' }) ?? '';
  const [isTechSheetConfirmOpen, setIsTechSheetConfirmOpen] = useState(false);

  const { specs, addSpec, removeSpecAt, updateSpec, resetSpecs } =
    useProductFormLogic<ICreateProductProps>({
      categories: [],
      setValue,
      specsFieldName: 'specs',
      initialSpecs: [{ label: '', value: '' }],
    });

  const {
    imageRules: imagesRules,
    helperText: imagesHelperText,
    allowedTypes: allowedImageTypes,
    buildImageSrc: buildExistingImageUrl,
  } = useImageUploadRules({
    mode: 'image',
    maxFiles: MAX_IMAGES,
    maxSizeMB: MAX_IMAGE_MB,
    requireAtLeastOne: true,
    enforceTotalSize: true,
  });

  const {
    filesRules: pdfRules,
    helperText: pdfHelperText,
    allowedTypes: pdfAllowedTypes,
    buildImageSrc: buildExistingPdfUrl,
  } = useImageUploadRules({
    mode: 'file',
    maxFiles: MAX_FILES,
    maxSizeMB: MAX_PDF_MB,
    requireAtLeastOne: false,
    enforceTotalSize: false,
    customMessages:{
      filesRequireLabels: "All files must have a label."
    }
  });

  const applyProductToUI = (p: IProductData) => {
    const state = productsService.prepareEditProductFormState({
      product: p,
      defaultValues: defaultEditValues as any,
      buildImageUrl: buildExistingImageUrl,
      buildPdfUrl: buildExistingPdfUrl,
    });

    productsService.applyPreparedEditProductState({
      state,
      reset,
      resetSpecs,
      setLoadedProduct,
      setExistingImageFiles,
      setExistingPdfFiles,
      setRemovedExistingImageIndexes,
      setIsExistingPdfRemoved,
      clearMessages: () => {
        setApiError(undefined);
        setSuccessMsg(null);
      },
      setDidSave,
    });
  };

  useEffect(() => {
    productsService.handleGetProductBySlug({
      slug,
      setLoading: setIsLoading,
      setApiError: (m) => setApiError(m ?? undefined),
      setProduct: (p) => {
        if (!p) return;
        applyProductToUI(p);
      },
    });
  }, [slug, buildExistingImageUrl, buildExistingPdfUrl]);

  const handleInvalidSubmit = () => {
    productsService.handleInvalidEditProductForm({
      setApiError,
      setSuccessMsg,
    });
  };

  const handleCancel = () => {
    productsService.resetEditProductForm({
      didSave,
      loadedProduct,
      defaultValues: defaultEditValues as any,
      buildImageUrl: buildExistingImageUrl,
      buildPdfUrl: buildExistingPdfUrl,
      reset,
      resetSpecs,
      setExistingImageFiles,
      setExistingPdfFiles,
      setRemovedExistingImageIndexes,
      setIsExistingPdfRemoved,

      clearMessages: () => {
        setApiError(undefined);
        setSuccessMsg(null);
      },
    });
  };

  const confirmDeleteImage = async () => {
    await productsService.handleDeleteProductImage({
      imageId: pendingDeleteImage!.id,
      token: authStore.authenticationState?.token ?? '',
      setLoading: setIsLoading,
      setApiError: (m) => setApiError(m ?? undefined),
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
    });
    setExistingImageFiles((prev) => prev.filter((x) => x.id !== pendingDeleteImage!.id));
    setIsConfirmOpen(false);
    setPendingDeleteImage(null);
  };

  const confirmDeleteTechnicalSheet = async () => {
    await productsService.handleDeleteTechnicalSheet({
      loadedProductId: loadedProduct?.id,
      token: authStore.authenticationState?.token ?? '',
      setLoading: setIsLoading,
      setApiError: (m) => setApiError(m ?? undefined),
      onAuthError,
      onForbidden: (msg: string) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
      setExistingPdfFiles,
      setIsExistingPdfRemoved,
      setValue,
      setIsTechSheetConfirmOpen,
    });
  };

  const handleValidSubmit = async (values: ICreateProductProps) => {
    await productsService.handleEditProductSubmission({
      formValues: values,
      loadedProductId: loadedProduct?.id,
      authToken: authStore.authenticationState?.token,
      setLoading: setIsLoading,
      setDidSave,
      setApiError: (m) => setApiError(m ?? undefined),
      setSuccessMsg,
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
    });
  };

  return (
    <div className={styles['cd-pageWrapper']} data-testid="ep-page">
      <div className={styles['cd-containerNarrow']} data-testid="ep-container">
        <div className={styles['cd-card']} data-testid="ep-card">
          <section className={`${styles['cd-cardSection']} ${styles['cd-bgGlass']}`}>
            <div className={styles['cd-cardHeader']} data-testid="ep-header">
              <div>
                <h2 className={styles['cd-cardTitle']} data-testid="ep-title">
                  Edit Product
                </h2>
                <p className={styles['cd-cardSub']} data-testid="ep-subtitle">
                  Update details below
                </p>
              </div>
            </div>
          </section>

          <form
            className={styles['cd-cardBody']}
            onSubmit={handleSubmit(handleValidSubmit, handleInvalidSubmit)}
            data-testid="ep-form"
          >
            <section className={styles['cd-cardSection']} data-testid="ep-section-category">
              <label className={styles['cd-label']}>Category</label>

              <Controller
                name="categoryId"
                control={control}
                rules={{ required: 'Category is required' }}
                render={({ field, fieldState }) => (
                  <Fragment>
                    <input type="hidden" value={field.value ?? ''} onChange={field.onChange} />
                    <div data-testid="ep-category-fixed">
                      Category ID: <strong>{field.value || 'Missing categoryId'}</strong>
                    </div>

                    {fieldState.error?.message && (
                      <div className={styles.formErrorText} data-testid="ep-category-error">
                        {fieldState.error.message}
                      </div>
                    )}
                  </Fragment>
                )}
              />
            </section>

            <section className={styles['cd-cardSection']} data-testid="ep-section-name">
              <div className={styles['cd-sectionTop']}>
                <label className={styles['cd-label']}>Name</label>
                <span className={styles['cd-counter']} data-testid="ep-name-counter">
                  {nameValue.length}/{NAME_MAX}
                </span>
              </div>

              <Controller
                name="name"
                control={control}
                rules={{
                  required: 'Name is required',
                  validate: (v) =>
                    (v?.trim().length ?? 0) <= NAME_MAX ||
                    `Name must be at most ${NAME_MAX} characters`,
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    label=""
                    placeholder="Enter product name"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    data-testid="ep-name-input"
                  />
                )}
              />
            </section>

            <section className={styles['cd-cardSection']} data-testid="ep-section-price-stock">
              <div className={styles['cd-grid2']}>
                <div>
                  <label className={styles['cd-label']}>Price</label>
                  <Controller
                    name="price"
                    control={control}
                    rules={{
                      required: 'Price is required',
                      validate: (v) => {
                        const n = Number(v);
                        if (!Number.isFinite(n)) return 'Price must be a number';
                        if (n <= 0) return 'Price must be > 0';
                        return true;
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextInput
                        label=""
                        type="number"
                        placeholder="e.g., 49.99"
                        value={field.value as any}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                        data-testid="ep-price-input"
                      />
                    )}
                  />
                </div>

                <div>
                  <label className={styles['cd-label']}>Stock</label>
                  <Controller
                    name="stock"
                    control={control}
                    rules={{
                      required: 'Stock is required',
                      validate: (v) => {
                        const n = Math.trunc(Number(v));
                        if (!Number.isFinite(n)) return 'Stock must be a number';
                        if (n < 0) return 'Stock cannot be negative';
                        if (!Number.isInteger(n)) return 'Stock must be an integer';
                        return true;
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextInput
                        label=""
                        type="number"
                        placeholder="e.g., 100"
                        value={field.value as any}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                        data-testid="ep-stock-input"
                      />
                    )}
                  />
                </div>
              </div>
            </section>

            <section className={styles['cd-cardSection']} data-testid="ep-section-shortdesc">
              <div className={styles['cd-sectionTop']}>
                <label className={styles['cd-label']}>Short description</label>
                <span className={styles['cd-counter']} data-testid="ep-shortdesc-counter">
                  {shortDescValue.length}/{SHORT_DESC_MAX}
                </span>
              </div>

              <Controller
                name="shortDescription"
                control={control}
                rules={{
                  required: 'Short description is required',
                  validate: (v) =>
                    (v?.trim().length ?? 0) <= SHORT_DESC_MAX ||
                    `Must be at most ${SHORT_DESC_MAX} characters`,
                }}
                render={({ field, fieldState }) => (
                  <Fragment>
                    <textarea
                      className={styles['cd-textarea']}
                      rows={4}
                      placeholder="A quick product summary…"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      data-testid="ep-shortdesc-input"
                    />
                    {fieldState.error?.message && (
                      <div className={styles.formErrorText} data-testid="ep-shortdesc-error">
                        {fieldState.error.message}
                      </div>
                    )}
                  </Fragment>
                )}
              />
            </section>

            <section className={styles['cd-cardSection']} data-testid="ep-section-longdesc">
              <div className={styles['cd-sectionTop']}>
                <label className={styles['cd-label']}>Long description</label>
                <span className={styles['cd-counter']} data-testid="ep-longdesc-counter">
                  {longDescValue.length}/{LONG_DESC_MAX}
                </span>
              </div>

              <Controller
                name="longDescription"
                control={control}
                rules={{
                  required: 'Long description is required',
                  validate: (v) =>
                    (v?.trim().length ?? 0) <= LONG_DESC_MAX ||
                    `Must be at most ${LONG_DESC_MAX} characters`,
                }}
                render={({ field, fieldState }) => (
                  <Fragment>
                    <textarea
                      className={styles['cd-textarea']}
                      rows={6}
                      placeholder="Detailed info, features, materials, care, warranty…"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      data-testid="ep-longdesc-input"
                    />
                    {fieldState.error?.message && (
                      <div className={styles.formErrorText} data-testid="ep-longdesc-error">
                        {fieldState.error.message}
                      </div>
                    )}
                  </Fragment>
                )}
              />
            </section>

            <section className={styles['cd-cardSection']} data-testid="ep-section-specs">
              <div className={styles['cd-sectionTop']}>
                <label className={styles['cd-label']}>Specs</label>
                <button
                  type="button"
                  className={styles['cd-btnGhost']}
                  onClick={addSpec}
                  data-testid="ep-specs-add"
                >
                  Add spec
                </button>
              </div>

              <ul className={styles['cd-list']} data-testid="ep-specs-list">
                {specs.map((s: ProductSpec, idx: number) => (
                  <li key={idx} className={styles['cd-listItem']}>
                    <Controller
                      name={`specs.${idx}.label` as const}
                      control={control}
                      rules={{
                        required: 'Label is required',
                        validate: (v) => {
                          const t = (v ?? '').trim();
                          if (!t) return 'Label is required';
                          if (t.length > 60) return 'Label ≤ 60 chars';
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <TextInput
                          label=""
                          placeholder="Label (e.g., Material)"
                          value={s.label}
                          onChange={(v) => {
                            updateSpec(idx, { label: v });
                            field.onChange(v);
                          }}
                          error={fieldState.error?.message}
                          data-testid={`ep-specs-label-${idx}`}
                        />
                      )}
                    />

                    <Controller
                      name={`specs.${idx}.value` as const}
                      control={control}
                      rules={{
                        required: 'Value is required',
                        validate: (v) => {
                          const t = (v ?? '').trim();
                          if (!t) return 'Value is required';
                          if (t.length > 120) return 'Value ≤ 120 chars';
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <TextInput
                          label=""
                          placeholder="Value (e.g., Silver)"
                          value={s.value}
                          onChange={(v) => {
                            updateSpec(idx, { value: v });
                            field.onChange(v);
                          }}
                          error={fieldState.error?.message}
                          data-testid={`ep-specs-value-${idx}`}
                        />
                      )}
                    />

                    <div>
                      <button
                        type="button"
                        className={styles['cd-removeBtn']}
                        onClick={() => removeSpecAt(idx)}
                        aria-label="Remove spec"
                        data-testid={`ep-specs-remove-${idx}`}
                        title="Remove"
                      >
                        <Close
                          className={styles['cd-removeIcon']}
                          width={18}
                          height={18}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section className={styles['cd-cardSection']} data-testid="ep-section-images">
              <div className={styles['cd-sectionTop']}>
                <label className={styles['cd-label']}>Images</label>
                <span className={styles['cd-counter']} data-testid="ep-images-hint">
                  Up to {MAX_IMAGES} images • ≤ {MAX_IMAGE_MB}MB each
                </span>
              </div>

              <Controller
                name="images"
                control={control}
                rules={{
                  validate: (fileList) => {
                    const existingCount = existingImageFiles.length;
                    const newCount = fileList?.length ?? 0;
                    if (existingCount === 0 && newCount === 0) return 'Image is required';
                    if (existingCount + newCount > MAX_IMAGES) {
                      const overBy = existingCount + newCount - MAX_IMAGES;
                      return `You exceeded the maximum of ${MAX_IMAGES} images. Please remove ${overBy} existing image(s) before adding new ones.`;
                    }
                    if (newCount > 0) return imagesRules.validate(fileList);
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <Upload
                    value={field.value}
                    onChange={field.onChange}
                    required
                    mimeTypes={allowedImageTypes}
                    maxFiles={MAX_IMAGES}
                    maxSizeMB={MAX_IMAGE_MB}
                    showInlineError={false}
                    helperText={imagesHelperText}
                    error={fieldState.error?.message || null}
                    existingFiles={existingImageFiles}
                    onRemoveExisting={(imageIndex) =>
                      productsService.confirmImageDeletion({
                        imageIndex,
                        productImages: existingImageFiles,
                        setApiError,
                        setSuccessMsg,
                        setPendingDeleteImage,
                        setIsConfirmOpen,
                      })
                    }
                    data-testid="ep-images"
                    parentTestId="ep-images"
                  />
                )}
              />
            </section>

            <section className={styles['cd-cardSection']} data-testid="ep-section-techsheet">
              <div className={styles['cd-sectionTop']}>
                <label className={styles['cd-label']}>Files (PDF)</label>
                <span className={styles['cd-counter']} data-testid="ep-techsheet-hint">
                  Optional • PDF only • ≤ {MAX_PDF_MB}MB
                </span>
              </div>

              <Controller
                name="files"
                control={control}
                rules={{ validate: (fl) => pdfRules.validate(fl) }}
                render={({ field, fieldState }) => (
                  <Upload
                    value={field.value ?? null}
                    onChange={field.onChange}
                    required={false}
                    mimeTypes={pdfAllowedTypes}
                    maxFiles={MAX_FILES}
                    maxSizeMB={MAX_PDF_MB}
                    showInlineError={false}
                    helperText={pdfHelperText}
                    error={fieldState.error?.message || null}
                    existingFiles={isExistingPdfRemoved ? [] : existingPdfFiles}
                    data-testid="ep-techsheet"
                    parentTestId="ep-files"
                    onRemoveExisting={() => {
                      setIsTechSheetConfirmOpen(true);
                    }}
                    requiresLabel={true}
                  />
                )}
              />
            </section>

            {successMsg && (
              <div className={styles['cd-successBanner']} data-testid="ep-success">
                {successMsg}
              </div>
            )}

            {apiError && (
              <div className={styles.formErrorText} data-testid="ep-api-error">
                {apiError}
              </div>
            )}

            <div className={styles['cd-cardFooter']} data-testid="ep-footer">
              <div className={styles['cd-actionsRow']} data-testid="ep-actions">
                <button
                  type="button"
                  className={styles['cd-btnGhost']}
                  onClick={handleCancel}
                  disabled={isSubmitting || isLoading}
                  data-testid="ep-cancel"
                >
                  Cancel
                </button>

                <Submission
                  disabled={isSubmitting || isLoading}
                  buttonText={isSubmitting || isLoading ? 'Saving...' : 'Save Product'}
                  className={styles['cd-btn']}
                  data-testid="ep-submit"
                />
              </div>
            </div>
            {isConfirmOpen && (
              <ConfirmModal
                open={isConfirmOpen}
                setOpen={setIsConfirmOpen}
                onConfirm={confirmDeleteImage}
                message="Are you sure you want to delete this image?"
                confirmLabel="Delete"
                cancelLabel="Cancel"
              />
            )}

            {isTechSheetConfirmOpen && (
              <ConfirmModal
                open={isTechSheetConfirmOpen}
                setOpen={setIsTechSheetConfirmOpen}
                onConfirm={confirmDeleteTechnicalSheet}
                message="Are you sure you want to delete the technical sheet?"
                confirmLabel="Delete"
                cancelLabel="Cancel"
              />
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProductForm;
